import React, { useState, useContext } from "react";
// Icons
import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  DeleteOutlined,
  ImportOutlined,
} from "@ant-design/icons";
import { ACTION_BUTTONS_NAME } from "constants/generalActionButtons";

import { GlobalContext } from "contexts/GlobalContext";
import classnames from "classnames";
import { useVolSurfaceControlTable } from "contexts/VolSurfaceControlTableContext";
import { toast } from "helpers/generalHelper";
import {
  addEmptyStrikeRow,
  deleteRow,
  getVolSurfaceStickyStrikes,
} from "api/volSurfaceApi";
import { handlePullToPricerClickTeamRun } from "api/teamRunApi";
import { parseStickyStrikeUpdatePayload } from "helpers/VolSurface/otherSurfaceStrikeGraphs";
import { Modal, Spin, Tooltip } from "antd";
import { createPortal } from "react-dom";

const ActionColumn = (props) => {
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isPullingToPricer, setIsPullingToPricer] = useState(false);

  const {
    globalState: { dark_Theme },
  } = useContext(GlobalContext);
  const {
    selectedRows,
    selectedFilters,
    patchStickyStrikeRows,
    applyStickyStrikeGraphPatch,
    ingestStickyStrikeIndexResponse,
    setIsCellEditing,
    isCalculationDateChanged,
    stickyStrikeData,
    beginStrikeReload,
    isCurrentStrikeReload,
    trackStrikeLoad,
  } = useVolSurfaceControlTable();

  const isSurfaceLocked = Boolean(stickyStrikeData?.is_surface_locked);
  const actionsDisabled = isLoading || isPullingToPricer || isSurfaceLocked;

  const canPullToPricer =
    props.data?.summary_id != null && props.data?.summary_id !== "";

  const handlePullToPricer = () => {
    if (actionsDisabled || !canPullToPricer) return;

    setIsPullingToPricer(true);
    handlePullToPricerClickTeamRun(props.data.summary_id)
      .then((res) => {
        toast(res?.data?.message, "success");
        props.api?.deselectAll?.();
      })
      .catch((err) => console.log("Pull to pricer >>", err))
      .finally(() => setIsPullingToPricer(false));
  };

  function onDeleteClick() {
    const extractSummaryIds = selectedRows?.length
      ? selectedRows.map((item) => {
          return {
            vol_smile_id: item.vol_smile_id,
            key: item.key,
          };
        })
      : [
          {
            vol_smile_id: props.data.vol_smile_id,
            key: props.data.key,
          },
        ];

    setConfirmLoading(true);
    setIsCellEditing(true);
    beginStrikeReload();
    deleteRow(extractSummaryIds)
      .then((res) => {
        setIsCellEditing(false);
        setIsLoading(true);
        fetchTableData();
        toast(res?.data?.message, "success");
        setConfirmLoading(false);
        props.api.deselectAll();
        setShowModal(false);
      })
      .catch((err) => {
        setIsCellEditing(false);
        setConfirmLoading(false);
        setShowModal(false);
        console.log("Error >>", err);
      });
  }

  const fetchTableData = () => {
    const gen = beginStrikeReload();
    const request = getVolSurfaceStickyStrikes({
      product_contract_style_id: selectedFilters?.product_id,
      date_time: isCalculationDateChanged ? selectedFilters?.strike_date : null,
      risk_free: selectedFilters?.risk_free,
    })
      .then((res) => {
        if (!isCurrentStrikeReload(gen)) return;
        ingestStickyStrikeIndexResponse(res.data.data);
      })
      .catch((err) => console.log(err))
      .finally(() => {
        setIsLoading(false);
      });
    trackStrikeLoad(request);
  };

  const handleAddRow = (isAbove) => {
    setIsLoading(true);
    setIsCellEditing(true);
    addEmptyStrikeRow({
      vol_smile_id: props.data.vol_smile_id,
      key: props.data.key,
      is_above: isAbove,
    })
      .then((res) => {
        setIsCellEditing(false);
        const { rows, graphs } = parseStickyStrikeUpdatePayload(
          res?.data?.data
        );
        if (rows.length > 0) patchStickyStrikeRows(rows);
        if (graphs.length > 0) applyStickyStrikeGraphPatch(graphs);
        else if (rows.length === 0) fetchTableData();
      })
      .catch((err) => {
        setIsCellEditing(false);
        setIsLoading(false);
        console.log("Error >>", err);
      });
  };

  return (
    <div>
      {isLoading ? (
        <Spin size="small" className="mx-4" />
      ) : (
        <>
          <DeleteOutlined
            disabled={actionsDisabled}
            role="button"
            onClick={() => {
              setShowModal(true);
            }}
            className={classnames("button-icon fs-6 text-danger", {
              "button-light-theme": !dark_Theme,
            })}
          />
          <Tooltip title="add row above">
            <ArrowUpOutlined
              disabled={actionsDisabled}
              onClick={() => {
                handleAddRow(1);
              }}
              role="button"
              className={classnames("button-icon mx-2 ", {
                "button-light-theme": !dark_Theme,
              })}
            />
          </Tooltip>
          <Tooltip title="add row below">
            <ArrowDownOutlined
              disabled={actionsDisabled}
              onClick={() => {
                handleAddRow(0);
              }}
              role="button"
              className={classnames("button-icon ", {
                "button-light-theme": !dark_Theme,
              })}
            />
          </Tooltip>
          {canPullToPricer ? (
            <Tooltip title={ACTION_BUTTONS_NAME.PULL_TO_PRICER.label}>
              {isPullingToPricer ? (
                <Spin size="small" className="mx-2" />
              ) : (
                <ImportOutlined
                  id="vol-surface-sticky-strike-pull-to-pricer"
                  disabled={actionsDisabled}
                  role="button"
                  onClick={handlePullToPricer}
                  className={classnames(
                    "button-icon mx-2 rotate-import--icon",
                    {
                      "button-light-theme": !dark_Theme,
                    }
                  )}
                />
              )}
            </Tooltip>
          ) : null}
        </>
      )}

      {createPortal(
        <Modal
          className={`${dark_Theme && "ant-modal-dark content_dark"}`}
          title="Delete Confirmation"
          style={{
            top: 20,
          }}
          confirmLoading={confirmLoading}
          open={showModal}
          okText="Delete"
          onOk={() => onDeleteClick()}
          onCancel={() => setShowModal(false)}
          centered
        >
          <p>Are you sure you want to delete? This action cannot be undone.</p>
        </Modal>,
        document.body
      )}
    </div>
  );
};

export default ActionColumn;
